import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2, Phone, MapPin, ArrowRight, Mail, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, Eyebrow } from "./Reveal";
import { api, errorText, SERVICES, PHONE, PHONE_LABEL, WHATSAPP_URL, COMPANY, MAPS_URL } from "@/lib/api";
import { WhatsAppIcon } from "./WhatsApp";
import { CarPicker, EMPTY_CAR } from "./CarPicker";
import { prefillInquiry } from "./smooth";
import { trackLead } from "@/lib/gads";
import { hasMarketingConsent } from "@/lib/consent";
import { useT } from "@/lib/i18n";

const TXT = {
  no: {
    services: {}, eyebrow: "Kontakt", h2a: "Be om", h2b: "tilbud.",
    intro: "Fortell oss om bilen og hva du trenger hjelp med, så kommer vi tilbake med pris og tidspunkt.",
    waSub: "Send oss en melding", maps: "4020 Stavanger · Vis i Google Maps", hours: "Åpningstider · mandag–fredag",
    toast: "Takk! Vi tar kontakt så snart som mulig.", err: null,
    doneH: "Mottatt.", doneP: "Takk for henvendelsen. Vi tar kontakt så snart som mulig.", again: "Send en ny forespørsel",
    name: "Navn *", phone: "Telefon *", email: "E-post", regnr: "Registreringsnummer", regnrTitle: "F.eks. AB 12345", service: "Tjeneste *",
    message: "Melding", messagePh: "Beskriv kort hva du trenger hjelp med", emailPh: "navn@epost.no",
    privacy: "Opplysningene brukes kun til å svare på henvendelsen din. Les vår", privacyLink: "personvernerklæring", submit: "Send forespørsel",
  },
  en: {
    services: { bilnokkel: "Car key / immobiliser", kloning: "Cloning", annet: "Other" }, eyebrow: "Contact", h2a: "Request a", h2b: "quote.",
    intro: "Tell us about your car and what you need help with, and we will get back to you with a price and a time.",
    waSub: "Send us a message", maps: "4020 Stavanger · View on Google Maps", hours: "Opening hours · Monday–Friday",
    toast: "Thank you! We will get back to you as soon as possible.", err: "Something went wrong. Please try again.",
    doneH: "Received.", doneP: "Thank you for your inquiry. We will get back to you as soon as possible.", again: "Send a new inquiry",
    name: "Name *", phone: "Phone *", email: "Email", regnr: "Registration number", regnrTitle: "E.g. AB 12345", service: "Service *",
    message: "Message", messagePh: "Briefly describe what you need help with", emailPh: "name@email.com",
    privacy: "Your details are only used to reply to your inquiry. Read our", privacyLink: "privacy policy", submit: "Send inquiry",
  },
};

const EMPTY = { name: "", phone: "", email: "", regnr: "", stage1: "", service: "bilnokkel", message: "" };
const inputCls = "w-full border-0 border-b border-white/15 bg-transparent px-0 py-3 text-base text-white placeholder:text-zinc-500 outline-none transition-colors focus:border-[var(--red)]";

const Field = ({ label, htmlFor, children }) => (
  <label htmlFor={htmlFor} className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{label}</span>
    {children}
  </label>
);

export const Inquiry = () => {
  const t = useT(TXT);
  const [f, setF] = useState(EMPTY);
  const [car, setCar] = useState(EMPTY_CAR);
  const [pickerKey, setPickerKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    const onPrefill = (e) => {
      const d = e.detail ?? {};
      setDone(false);
      if (d.service) setF((x) => ({ ...x, service: d.service, stage1: d.stage1 ?? "", ...(d.message ? { message: d.message } : {}) }));
      if (d.car) {
        setCar(d.car);
        setPickerKey((k) => k + 1);
      }
    };
    window.addEventListener("rbtun:prefill", onPrefill);
    const q = new URLSearchParams(window.location.search);
    let t;
    if (q.get("tjeneste")) {
      const svc = SERVICES.some((s) => s.value === q.get("tjeneste")) ? q.get("tjeneste") : "annet";
      const pris = (q.get("pris") ?? "").slice(0, 160);
      const detail = { service: svc, message: pris ? `Prisforespørsel: ${pris}` : "" };
      setF((x) => ({ ...x, service: detail.service, message: detail.message || x.message }));
      ["tjeneste", "pris"].forEach((k) => q.delete(k));
      const rest = q.toString();
      window.history.replaceState(null, "", window.location.pathname + (rest ? `?${rest}` : "") + "#kontakt");
      t = setTimeout(() => prefillInquiry(detail), 600);
    }
    if (q.get("make")) {
      const detail = { service: "chiptuning", car: { make: q.get("make") ?? "", model: q.get("model") ?? "", engine: q.get("engine") ?? "", year: "" } };
      setF((x) => ({ ...x, service: "chiptuning" }));
      setCar(detail.car);
      setPickerKey((k) => k + 1);
      ["make", "model", "engine"].forEach((k) => q.delete(k));
      const rest = q.toString();
      window.history.replaceState(null, "", window.location.pathname + (rest ? `?${rest}` : "") + "#kontakt");
      t = setTimeout(() => prefillInquiry(detail), 600);
    }
    return () => {
      clearTimeout(t);
      window.removeEventListener("rbtun:prefill", onPrefill);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        ...f, email: f.email || null, message: f.message || null, regnr: f.regnr.trim() || null, stage1: f.stage1 || null,
        car: `${car.make} ${car.model}`.trim(), engine: car.engine || null, year: car.year || null,
      };
      const res = await api.post("/inquiries", body);
      trackLead(res?.data?._id ?? res?.data?.id, hasMarketingConsent());
      setDone(true);
      setF(EMPTY);
      setCar(EMPTY_CAR);
      setPickerKey((k) => k + 1);
      toast.success(t.toast);
    } catch (err) {
      toast.error(t.err ?? errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="kontakt" data-testid="inquiry-section" className="relative border-t border-white/10">
      <span id="contact" className="absolute -top-20" aria-hidden="true" />
      <div className="mx-auto grid max-w-7xl gap-14 px-5 py-24 sm:px-8 md:py-32 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <Eyebrow index="06">{t.eyebrow}</Eyebrow>
          <h2 className="font-display text-5xl font-black uppercase leading-[0.9] md:text-7xl">{t.h2a}<br /><span className="text-[var(--red)]">{t.h2b}</span></h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-zinc-300">
            {t.intro}
          </p>
          <div className="mt-10 space-y-4">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="group flex items-center gap-4">
              <span className="grid h-12 w-12 place-items-center bg-[#25D366] text-[#08090b] transition-transform group-hover:scale-105"><WhatsAppIcon size={20} /></span>
              <span>
                <span className="block font-display text-3xl font-bold">WhatsApp</span>
                <span className="block font-mono text-[11px] uppercase tracking-widest text-zinc-400">{t.waSub}</span>
              </span>
            </a>
            <a href={`tel:${PHONE}`} data-testid="contact-phone-link" className="group flex items-center gap-4">
              <span className="grid h-12 w-12 place-items-center border border-white/15 transition-colors group-hover:border-[var(--red)] group-hover:bg-[var(--red)]"><Phone size={18} /></span>
              <span className="font-display text-3xl font-bold">{PHONE_LABEL}</span>
            </a>
            <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="group flex items-center gap-4">
              <span className="grid h-12 w-12 shrink-0 place-items-center border border-white/15 transition-colors group-hover:border-[var(--red)] group-hover:bg-[var(--red)]"><Mail size={18} /></span>
              <span className="break-all text-lg font-semibold md:text-xl">{COMPANY.email}</span>
            </a>
            <a href={MAPS_URL} target="_blank" rel="noopener noreferrer"
              className="group flex items-center gap-4" data-testid="contact-location">
              <span className="grid h-12 w-12 shrink-0 place-items-center border border-white/15 transition-colors group-hover:border-[var(--red)] group-hover:bg-[var(--red)]"><MapPin size={18} /></span>
              <span>
                <span className="block font-display text-3xl font-bold">Jåttåveien 92</span>
                <span className="block font-mono text-[11px] uppercase tracking-widest text-zinc-400">{t.maps}</span>
              </span>
            </a>
            <div className="flex items-center gap-4" data-testid="contact-hours">
              <span className="grid h-12 w-12 shrink-0 place-items-center border border-white/15"><Clock size={18} /></span>
              <span>
                <span className="block font-display text-3xl font-bold">08:00–16:00</span>
                <span className="block font-mono text-[11px] uppercase tracking-widest text-zinc-400">{t.hours}</span>
              </span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          {done ? (
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} data-testid="inquiry-success"
              className="flex min-h-[420px] flex-col items-start justify-center border border-white/10 bg-[var(--surface)] p-8 md:p-12">
              <span className="mb-6 grid h-14 w-14 place-items-center bg-[var(--red)]"><Check size={28} /></span>
              <h3 className="font-display text-5xl font-black uppercase leading-none">{t.doneH}</h3>
              <p className="mt-4 max-w-md text-zinc-300">{t.doneP}</p>
              <button data-testid="inquiry-new-button" onClick={() => setDone(false)}
                className="mt-8 font-mono text-xs uppercase tracking-widest text-zinc-300 underline underline-offset-4 hover:text-white">{t.again}</button>
            </motion.div>
          ) : (
            <form id="kontakt-form" onSubmit={submit} data-testid="inquiry-form" className="grid gap-7 border border-white/10 bg-[var(--surface)] p-6 sm:grid-cols-2 md:p-10">
              <Field label={t.name} htmlFor="inquiry-name"><input id="inquiry-name" name="name" autoComplete="name" required minLength={2} value={f.name} onChange={set("name")} className={inputCls} placeholder="Ola Nordmann" data-testid="inquiry-name-input" /></Field>
              <Field label={t.phone} htmlFor="inquiry-phone"><input id="inquiry-phone" name="phone" autoComplete="tel" required minLength={5} type="tel" value={f.phone} onChange={set("phone")} className={inputCls} placeholder="+47 ..." data-testid="inquiry-phone-input" /></Field>
              <Field label={t.email} htmlFor="inquiry-email"><input id="inquiry-email" name="email" autoComplete="email" type="email" value={f.email} onChange={set("email")} className={inputCls} placeholder={t.emailPh} data-testid="inquiry-email-input" /></Field>
              <Field label={t.regnr} htmlFor="inquiry-regnr"><input id="inquiry-regnr" name="regnr" value={f.regnr} onChange={(e) => setF({ ...f, regnr: e.target.value.toUpperCase() })} maxLength={8} pattern="[A-ZÆØÅ]{0,3} ?[0-9]{0,5}" title={t.regnrTitle} autoCapitalize="characters" className={`${inputCls} uppercase tracking-wider`} placeholder="AB 12345" data-testid="inquiry-regnr-input" /></Field>
              <div className="sm:col-span-2">
                <span id="inquiry-service-label" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{t.service}</span>
                <div role="group" aria-labelledby="inquiry-service-label" className="mt-3 flex flex-wrap gap-2">
                  {SERVICES.map((s) => (
                    <button type="button" key={s.value} aria-pressed={f.service === s.value} data-testid={`inquiry-service-${s.value}`} onClick={() => setF({ ...f, service: s.value })}
                      className={`border px-3 py-2 font-mono text-[11px] uppercase tracking-wider transition-colors ${f.service === s.value ? "border-[var(--red)] bg-[var(--red)] text-white" : "border-white/15 text-zinc-300 hover:border-white/40"}`}>
                      {t.services[s.value] ?? s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <CarPicker key={pickerKey} value={car} onChange={setCar} showEngine={f.service !== "bilnokkel"} prefix="inquiry" />
                {f.stage1 && (
                  <p className="mt-4 border-l-2 border-[var(--red)] pl-3 font-mono text-xs uppercase tracking-wider text-zinc-300" data-testid="inquiry-stage1-info">
                    Stage 1: {f.stage1}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2">
                <Field label={t.message} htmlFor="inquiry-message"><textarea id="inquiry-message" name="message" rows={4} value={f.message} onChange={set("message")} maxLength={2000} className={`${inputCls} resize-none`} placeholder={t.messagePh} data-testid="inquiry-message-input" /></Field>
              </div>
              <p className="text-xs leading-relaxed text-zinc-400 sm:col-span-2" data-testid="inquiry-privacy-note">
                {t.privacy}{" "}
                <Link to="/personvern" className="text-zinc-200 underline underline-offset-2 hover:text-white" data-testid="inquiry-privacy-link">{t.privacyLink}</Link>.
              </p>
              <button type="submit" disabled={busy} data-testid="inquiry-submit-button"
                className="group flex items-center justify-center gap-3 bg-[var(--red)] px-7 py-4 font-mono text-sm font-bold uppercase tracking-widest text-white transition-[transform,opacity] hover:-translate-y-0.5 disabled:opacity-60 sm:col-span-2 sm:justify-self-start">
                {busy ? <Loader2 className="animate-spin" size={18} /> : null}
                {t.submit}
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
